import closeIcon from "src/assests/images/dashboard/Vector 3.png";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useAuth } from "src/contexts/auth-provider";
import { BtnLoader } from "src/Loader";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  CREATE_STORE_CUSTOMER,
  UPDATE_STORE_CUSTOMER,
} from "src/constants/mutations";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { failureToast, successToast } from "src/utils/toasts";
import type { StoreCustomer } from "src/interfaces/customer.interface";

const BORDER_LIGHT = "rgba(0,0,0,0.06)";

function nameToFirstLast(name: string): { firstName: string; lastName: string } {
  const t = (name ?? "").trim();
  if (!t) return { firstName: "", lastName: "" };
  const i = t.indexOf(" ");
  if (i <= 0) return { firstName: t, lastName: "" };
  return { firstName: t.slice(0, i), lastName: t.slice(i + 1).trim() };
}

export type ManageCustomerMode = "create" | "edit" | "view";

interface ManageCustomerModalProps {
  mode: ManageCustomerMode;
  customer: StoreCustomer | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function ManageCustomerModal({
  mode,
  customer,
  onClose,
  onSuccess,
}: ManageCustomerModalProps) {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [loading, setLoading] = useState(false);

  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isCreate = mode === "create";

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
    },
    validationSchema: Yup.object({
      name: isCreate ? Yup.string() : Yup.string(),
      phone: isCreate
        ? Yup.string().required("Phone is required")
        : Yup.string(),
      email: Yup.string().email("Invalid email").nullable(),
    }),
    onSubmit: () => {},
  });

  const { setValues } = formik;

  useEffect(() => {
    if (customer) {
      setValues({
        name: customer.name?.trim() ?? "",
        email: customer.email?.trim() ?? "",
        phone: customer.phone?.trim() ?? "",
        addressLine1: customer.addressLine1?.trim() ?? "",
        addressLine2: customer.addressLine2?.trim() ?? "",
        city: customer.city?.trim() ?? "",
        state: customer.state?.trim() ?? "",
        zipCode: customer.zipCode?.trim() ?? "",
      });
    } else if (isCreate) {
      setValues({
        name: "",
        email: "",
        phone: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
      });
    }
  }, [customer, isCreate, setValues]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleCreate = async () => {
    if (!hasRestaurant?.correlationId) return;
    setLoading(true);
    try {
      const { firstName, lastName } = nameToFirstLast(formik.values.name);
      const variables = {
        storeCorrelationId: hasRestaurant.correlationId,
        createCustomerInput: {
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: formik.values.email || undefined,
          phoneNumber: formik.values.phone || "",
          address:
            formik.values.addressLine1 ||
            formik.values.city ||
            formik.values.zipCode
              ? {
                  addressLine1: formik.values.addressLine1 || undefined,
                  addressLine2: formik.values.addressLine2 || undefined,
                  city: formik.values.city || undefined,
                  state: formik.values.state || undefined,
                  zipCode: formik.values.zipCode || undefined,
                }
              : undefined,
        },
      };
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: CREATE_STORE_CUSTOMER,
        variables,
      });
      if (data?.errors?.length) {
        throw data.errors;
      }
      successToast("Customer added");
      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error("Error creating customer:", err);
      const msg = Array.isArray(err)
        ? err[0]?.message ?? "Failed to add customer"
        : "Failed to add customer";
      failureToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!hasRestaurant?.correlationId || !customer?.correlationId) return;
    setLoading(true);
    try {
      const variables = {
        updateStoreCustomerInput: {
          storeCorrelationId: hasRestaurant.correlationId,
          storeCustomerCorrelationId: customer.correlationId,
          name: formik.values.name || undefined,
          email: formik.values.email || undefined,
          phone: formik.values.phone || undefined,
          addressLine1: formik.values.addressLine1 || undefined,
          addressLine2: formik.values.addressLine2 || undefined,
          city: formik.values.city || undefined,
          state: formik.values.state || undefined,
          zipCode: formik.values.zipCode || undefined,
        },
      };
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_STORE_CUSTOMER,
        variables,
      });
      if (data?.errors?.length) {
        throw data.errors;
      }
      successToast("Customer updated");
      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error("Error updating customer:", err);
      const msg = Array.isArray(err)
        ? err[0]?.message ?? "Failed to update customer"
        : "Failed to update customer";
      failureToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const title =
    mode === "create"
      ? "Add Customer"
      : mode === "edit"
        ? "Edit Customer"
        : "Customer details";

  const fieldClass =
    "text-sm text-[#252525] h-10 w-full border rounded-lg mt-1 px-3 focus:outline-none focus:ring-[3px] focus:ring-[rgba(253,118,87,0.18)] focus:border-[rgba(253,118,87,0.45)]";
  const labelClass = "text-[#9B9B9B] text-sm";
  const readOnlyClass = "bg-gray-50 cursor-default";

  const modal = (
    <div aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Portal + z above layout header (z-30) so the sheet is not covered on mobile */}
      <div
        className="fixed inset-0 z-[100] flex min-h-0 bg-black/60 items-center justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:p-4"
        role="presentation"
      >
        <div className="relative transform rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-[min(560px,calc(100vw-1.5rem))] max-h-[min(90dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-1.5rem))] flex flex-col min-h-0">
          <div className="flex min-h-0 flex-1 flex-col rounded-lg bg-white overflow-hidden">
            <div className="bg-white px-5 py-5 sm:px-8 sm:py-6 min-w-0 flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-contain">
              <div className="top flex justify-between items-center shrink-0 gap-2">
                <span className="text-xl font-semibold text-[#252525]">
                  {title}
                </span>
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <img
                    className="w-3 h-3"
                    src={closeIcon}
                    alt=""
                  />
                </button>
              </div>

              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Name</label>
                    <input
                      className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                      type="text"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    readOnly={isView}
                  />
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                      type="email"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    readOnly={isView}
                  />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input
                    className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                    type="text"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    readOnly={isView}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <p className="text-xs text-red-500 mt-0.5">
                      {formik.errors.phone}
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Address line 1</label>
                  <input
                    className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                    type="text"
                    name="addressLine1"
                    value={formik.values.addressLine1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    readOnly={isView}
                  />
                </div>
                <div>
                  <label className={labelClass}>Address line 2</label>
                  <input
                    className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                    type="text"
                    name="addressLine2"
                    value={formik.values.addressLine2}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    readOnly={isView}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className={labelClass}>City</label>
                    <input
                      className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                      type="text"
                      name="city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      readOnly={isView}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    <input
                      className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                      type="text"
                      name="state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      readOnly={isView}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Zip code</label>
                  <input
                    className={`${fieldClass} ${isView ? readOnlyClass : "border-[#F1F1F2]"}`}
                    type="text"
                    name="zipCode"
                    value={formik.values.zipCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    readOnly={isView}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6 shrink-0">
                {isView ? (
                  <button
                    type="button"
                    className="px-6 text-sm font-medium text-white h-9 rounded-lg bg-orange hover:bg-orange/90 focus:outline-none"
                    onClick={onClose}
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="px-4 text-sm font-medium text-gray-700 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 focus:outline-none"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    {loading ? (
                      <BtnLoader />
                    ) : (
                      <button
                        type="button"
                        className={`px-6 text-sm font-medium text-white h-9 rounded-lg bg-orange hover:bg-orange/90 focus:outline-none ${
                          isCreate && !formik.values.phone?.trim()
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={isCreate ? handleCreate : handleUpdate}
                        disabled={
                          isCreate
                            ? !formik.values.phone?.trim()
                            : !formik.dirty
                        }
                      >
                        {isCreate ? "Add" : "Save"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}
